import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import FilterChips from '../../library/filter-chips/filter-chips';
import Page, { Breadcrumbs, Content, PageTitle } from '../../library/page/page';
import Pagination from '../../library/pagination/pagination';
import Typography from '../../library/typography/typography';
import DTCCBanner from '../../components/dtcc-banner/dtcc-banner';
import FitmentPageBanner from '../../components/fitment-page-banner/fitment-page-banner';
import Message from '../../components/message/message';
import ResultsBar from '../../components/results-bar/results-bar';
import SectionLoadingSpinner from '../../components/section-loading-spinner/section-loading-spinner';
import SetProductTabs from '../../components/set-product-tabs/set-product-tabs';
import StaggeredProductTabs from '../../components/staggered-product-tabs/staggered-product-tabs';
import TireWheelPackageDetails from '../../components/tire-wheel-package-details/tire-wheel-package-details';
import TreadwellCompactBanner from '../../components/treadwell-compact-banner/treadwell-compact-banner';
import TreadwellSeeAllButton from '../../components/treadwell-see-all-button/treadwell-see-all-button';
import LoadMore from './components/load-more/load-more';
import ProductComparePreview from './components/product-compare-preview/product-compare-preview';
import ProductList from './components/product-list/product-list';
import ProductListFilterModal from './components/product-list-filter-modal/product-list-filter-modal';
import StaggeredSearchInfo from './components/staggered-search-info/staggered-search-info';
import TopThree from './components/top-three/top-three';
import WheelConfiguratorBanner from './components/wheel-configurator-banner/wheel-configurator-banner';
import WinterTiresBanner from './components/winter-tires-banner/winter-tires-banner';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import NoSearchResultsPage from '../no-search-results-page/no-search-results-page';
import { FILTERS_CLEARED_QUERY_PARAM_NAME } from './product-list-page-constants';

import {
  useCompareProducts,
  useFetchCompareProducts,
  useFitment,
  useInitialProductListFilterValueLimits,
  useMediaQuery,
  useModal,
  useNavigateTo,
  useProductEntryDataLayerValues,
  useScrollTo,
  useSectionLoadingSpinner,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';
import usePlpAnalytics from './hooks/use-plp-analytics';
import usePlpProducts from './hooks/use-plp-products';

import { trackAction } from '../../shared/utils/analytics/analytics';
import {
  clearLastPlpProduct,
  clearStoredBackToResultsUrls,
  setStoredBackToResultsUrl
} from '../../shared/utils/back-to-results/back-to-results';
import { isChipFilter } from '../../shared/utils/filters/filters';
import {
  formatTireSizeParts,
  formatWheelSizeParts
} from '../../shared/utils/format/format';
import { ProductTypeProp } from '../../shared/utils/prop-types/prop-types';
import { sessionStorage } from '../../shared/utils/storage/storage';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { getPlpLD } from './utils/item-list-ld';

import { TireSearchOption } from '../../shared/types/graphql-types';
import { PLPInfoMessageIds } from './types/plp-products';
import { BACK_TO_RESULTS_PAGE_IDS } from '../../shared/constants/back-to-results';
import { PRODUCT_POSITION } from '../../shared/constants/data-layer';
import {
  PRODUCT_TYPES,
  TIRE_CATEGORIES,
  TREADWELL_PRODUCTS_DISPLAY_TYPES,
  WHEEL_CATEGORIES
} from '../../shared/constants/products';
import { LAST_PLP_PRODUCT } from '../../shared/constants/storage-keys';

import './product-list-page.scss';

const ProductListFilter = lazy(() =>
  import('../../components/product-list-filter/product-list-filter')
);
const TREADWELL_RANK = 'treadwell_recommendation_rank';

function ProductListPage(props) {
  const {
    isAccessorySearch,
    isBrandSearch,
    isFitmentSearch,
    isPackageSearch,
    isSizeSearch,
    isStaggeredSearch,
    isPrebuiltPackageSearch,
    isTextSearch,
    productType,
    queryType
  } = props;

  const history = useHistory();
  const { hash, pathname, search, state } = useLocation();
  const { info: infoMessage } = state || {};
  const navigateTo = useNavigateTo();

  const { disableTreadwellFlow, isTreadwellFlow, treadwellPreferences } =
    useTreadwellPreferences();

  const { isFitmentLoading, selectedFitment } = useFitment();
  const {
    allProducts,
    allProductsDoesFitEnum,
    breadcrumbs,
    category,
    clearSelectedFilters,
    currentSort,
    error,
    filters,
    getPagenationUrl,
    info,
    isLoading,
    metaData,
    numberOfPages,
    pageNum,
    products,
    recommendationAvailable,
    searchText,
    selectedFilters,
    selectFilters,
    setPageNum,
    productSetSearchType,
    setTreadwellProductsDisplayType,
    sortOptions,
    staggeredSearchType,
    subtitle,
    tireSizes,
    totalNumberOfResults,
    treadwellProductsDisplayType,
    wheelSizes,
    searchOption,
    onSelectSort,
    onUpdateSearchOption
  } = usePlpProducts({
    isAccessorySearch,
    isFitmentLoading,
    isFitmentSearch,
    isPackageSearch,
    isPrebuiltPackageSearch,
    isSizeSearch,
    isStaggeredSearch,
    productType,
    queryType,
    selectedFitment
  });
  const breakpoint = useMediaQuery();
  const lastPlpProduct = sessionStorage(LAST_PLP_PRODUCT);
  const scrollToHash = useScrollTo(hash, {
    behavior: 'auto',
    offset: 120,
    shouldWatchPosition: true
  });

  const scrollToPLPTop = useScrollTo(
    breakpoint.isMinSm ? '#product-list' : '#product-list-content',
    {
      behavior: 'auto',
      offset: breakpoint.isMinSm ? 120 : 90
    }
  );

  const {
    defaultMaxPrice,
    defaultMinPrice,
    highestPriceInResults,
    lowestPriceInResults,
    onValueChange: onSliderValueChange
  } = useInitialProductListFilterValueLimits(filters);

  const {
    onProductComparison,
    productsForComparison: localProductsForComparison,
    clearAllProductsForComparison,
    maxProductsForComparison,
    productCodesForComparison,
    quantities: initialQuantities,
    checkCompareProductsPath
  } = useCompareProducts();
  const { setShowSpinner } = useSectionLoadingSpinner();
  const { updatePlpAnalytics } = usePlpAnalytics(
    products,
    productType,
    selectedFilters,
    sortOptions?.[currentSort]?.name,
    isLoading,
    isPrebuiltPackageSearch,
    pageNum,
    numberOfPages,
    category
  );
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { productData } = useFetchCompareProducts({
    initialQuantities,
    maxProductsForComparison,
    productCodesForComparison,
    productsForComparison: localProductsForComparison,
    selectedFitment,
    treadwellPreferences
  });
  const productsForComparison =
    localProductsForComparison?.map((comparisonProduct, idx) => ({
      ...comparisonProduct,
      ...(productData?.products[idx] || [])
    })) || [];

  const [displayMobileFilters, setDisplayMobileFilters] = useState(false);
  const [shouldSuppressNoPackagesModal, setShouldSuppressNoPackagesModal] =
    useState(false);
  const { closeModal, isOpen, openModal } = useModal();
  const queryParams = queryString.parse(search);
  const showFitmentPageBanner =
    category?.seoName !== TIRE_CATEGORIES.TRAILER_TIRES &&
    category?.seoName !== WHEEL_CATEGORIES.TRAILER_WHEELS &&
    category?.seoName !== TIRE_CATEGORIES.LAWN_TIRES;
  const comparePreviewRef = useRef();

  useEffect(() => {
    setShowSpinner(isLoading);
  }, [isLoading, setShowSpinner]);

  useEffect(() => {
    if (
      !isLoading &&
      !isOpen &&
      !products?.length &&
      !shouldSuppressNoPackagesModal &&
      isPackageSearch &&
      isStaggeredSearch
    ) {
      setShouldSuppressNoPackagesModal(true);
      navigateTo('#/package/no-package-results');
    }
  }, [
    isLoading,
    isOpen,
    isPackageSearch,
    isStaggeredSearch,
    navigateTo,
    products,
    shouldSuppressNoPackagesModal
  ]);

  useEffect(() => {
    if (!isLoading && info) {
      const newLocation = {
        ...history.location,
        state: {
          info
        }
      };

      if (
        info.id === PLPInfoMessageIds.noInStockTreadwellProducts &&
        search.includes('storeInStock%3AIn%20Stock')
      ) {
        newLocation.search = search.replace(/q=.+&/, 'q=%3Arecommended&');
        history.replace(newLocation);
      } else if (
        info.id === PLPInfoMessageIds.noTreadwellProducts &&
        search.includes('%3Arecommended')
      ) {
        newLocation.search = '';
        history.replace(newLocation);

        disableTreadwellFlow();
      }
    }
  }, [isLoading, info, history, search, disableTreadwellFlow]);

  useEffect(() => {
    if (lastPlpProduct && !hash.includes(lastPlpProduct)) {
      history.replace({
        ...history.location,
        hash: `#product-code-${lastPlpProduct}`
      });
      clearLastPlpProduct();
    }
  }, [hash, history, lastPlpProduct]);

  useEffect(() => {
    if (hash && hash.includes('product-code')) {
      setTreadwellProductsDisplayType(
        TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
      );
      scrollToHash();
      clearLastPlpProduct();
    }
  }, [scrollToHash, hash, setTreadwellProductsDisplayType]);

  useEffect(() => {
    if (
      isPrebuiltPackageSearch &&
      !isLoading &&
      !isArrayWithLength(products) &&
      !hash
    ) {
      navigateTo('#/package/choose-tire-prebuilt-entry/no-results/home');
    }
  }, [
    hash,
    closeModal,
    navigateTo,
    openModal,
    isPrebuiltPackageSearch,
    isLoading,
    products
  ]);

  function onToggleMobileFilter() {
    setDisplayMobileFilters(!displayMobileFilters);
  }

  function onPageChange(pageNum) {
    scrollToPLPTop();
    clearLastPlpProduct();
    setPageNum(pageNum);
  }

  function updateSelectedFilters(filterToToggle) {
    clearLastPlpProduct();
    clearAllProductsForComparison();
    scrollToPLPTop();
    selectFilters(filterToToggle);
    setTreadwellProductsDisplayType(TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL);

    if (
      !selectedFilters.find(
        selectedFilter =>
          selectedFilter?.categoryCode === filterToToggle?.categoryCode
      )
    ) {
      trackAction('plp_filter_click', {
        filter_family: filterToToggle?.categoryName,
        filter_selection: filterToToggle?.valueName
      });
    }
  }

  function getHeadingText() {
    let text =
      productType === PRODUCT_TYPES.TIRES
        ? 'Tires by vehicle'
        : 'Search Results';

    if (
      isBrandSearch &&
      category?.name &&
      queryParams.cat &&
      selectedFilters?.[0]
    ) {
      text = `${category.name.replace(/\sTires|\sWheels$/i, '')} ${
        selectedFilters[0].valueName
      }`;
    }

    return text;
  }

  function getResultsText(results) {
    const isPlural = Array.isArray(results) && results.length !== 1;

    if (searchText) {
      return `results for "${searchText}"`;
    }

    if (isPrebuiltPackageSearch) {
      return `package result${isPlural ? 's' : ''}`;
    }

    if (productType === PRODUCT_TYPES.TIRES) {
      return `tire result${isPlural ? 's' : ''}`;
    }

    if (productType === PRODUCT_TYPES.WHEELS) {
      return `wheel result${isPlural ? 's' : ''}`;
    }

    if (productType === PRODUCT_TYPES.ACCESSORIES) {
      return `accessory result${isPlural ? 's' : ''}`;
    }
    return 'results';
  }

  function getTrackProductPayload(product, idx, quantity, rearQuantity) {
    const pos = `${idx + 1}`;

    return {
      ...getProductEntryDataLayerValues(product, quantity, rearQuantity),
      [PRODUCT_POSITION]: product?.rearProduct ? [pos, pos] : [pos]
    };
  }

  function onAddProductClick(product, idx, quantity, rearQuantity) {
    const trackProductPayload = getTrackProductPayload(
      product,
      idx,
      quantity,
      rearQuantity
    );

    if (isTreadwellFlow) {
      trackAction('product_list_add_treadwell_recommendation', {
        [TREADWELL_RANK]: product?.recommendationData?.rank,
        trackProductPayload
      });
    } else {
      trackAction('product_list_cart_add', trackProductPayload);
    }
  }

  function onViewProductClick(product, idx) {
    const trackProductPayload = getTrackProductPayload(product, idx);

    if (isTreadwellFlow) {
      trackProductPayload[TREADWELL_RANK] = product?.recommendationData?.rank;
      trackAction(
        'product_list_select_treadwell_recommendation',
        trackProductPayload
      );
    } else {
      trackAction('product_view_details_click', trackProductPayload);
    }
  }

  const onTabClick = useCallback(
    (currentTab, selectedTab) => {
      if (currentTab !== selectedTab) {
        clearAllProductsForComparison();
      }
    },
    [clearAllProductsForComparison]
  );

  const plpLD = getPlpLD(
    products,
    pathname,
    metaData?.description,
    metaData?.title
  );

  useEffect(() => {
    checkCompareProductsPath(productType, pathname);
  }, [checkCompareProductsPath, productType, pathname]);

  useEffect(() => {
    if (searchOption === TireSearchOption.All) {
      setTreadwellProductsDisplayType(
        TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
      );
    }
  }, [searchOption, setTreadwellProductsDisplayType]);

  if (error && !products && !window.isPreRendering) {
    throw error;
  }

  if (
    !isLoading &&
    !error &&
    !window.isPreRendering &&
    !isArrayWithLength(products)
  ) {
    return <NoSearchResultsPage />;
  }

  clearStoredBackToResultsUrls();
  setStoredBackToResultsUrl(BACK_TO_RESULTS_PAGE_IDS.PLP);

  const showTopThree =
    productType === PRODUCT_TYPES.TIRES &&
    !isBrandSearch &&
    !isSizeSearch &&
    (!recommendationAvailable ||
      selectedFilters.some(
        filter => filter.categoryCode === 'all-tire-boolean'
      ));
  const formattedTireSize = formatTireSizeParts(tireSizes?.front);

  return (
    <DefaultLayout
      showMinimalFooter
      showTreadwellBanner
      treadwellBannerConfig={{
        onShopTiresCTAClick: onUpdateSearchOption,
        shopTiresCTALabel:
          searchOption === TireSearchOption.All
            ? 'Shop Recommended Tires'
            : 'Shop All Tires',
        showShopTiresCTA: true
      }}
    >
      <Page styleName="page">
        <Helmet>
          <title>
            {pageNum > 0 && numberOfPages > 0
              ? `Page ${pageNum + 1} of ${numberOfPages} for `
              : ''}
            {metaData?.title || ''}
          </title>
          {isBrandSearch && queryParams.cat && (
            <link
              href={`${window.location.protocol}//${process.env.__DT_HOST__}${window.location.pathname}${window.location.search}`}
              rel="canonical"
            />
          )}
          <meta content={metaData?.description} name="description" />
          {pageNum < numberOfPages - 1 && (
            <link href={getPagenationUrl(pageNum + 1)} rel="next" />
          )}
          {pageNum > 0 && (
            <link href={getPagenationUrl(pageNum - 1)} rel="prev" />
          )}
          <meta content={metaData?.keywords} name="keywords" />
          {plpLD && !window.isPreRendering && (
            <script type="application/ld+json">{plpLD}</script>
          )}
        </Helmet>

        {breadcrumbs && !window.isPreRendering && !isLoading && (
          <PageTitle
            styleName="heading"
            subtitle={
              <div
                dangerouslySetInnerHTML={{
                  __html: subtitle || ''
                }}
              />
            }
            title={() => {
              if (
                !isTreadwellFlow &&
                (productType === PRODUCT_TYPES.TIRES ||
                  productType === PRODUCT_TYPES.WHEELS) &&
                !isSizeSearch
              ) {
                return getHeadingText();
              } else if (isSizeSearch) {
                return (
                  <Breadcrumbs
                    crumbs={breadcrumbs}
                    hideIcon
                    styleName={
                      isTextSearch || isSizeSearch
                        ? 'heading-breadcrumbs-text-search'
                        : 'heading-breadcrumbs'
                    }
                  />
                );
              } else {
                return 'Search Results';
              }
            }}
          >
            {!isTreadwellFlow &&
              productType == PRODUCT_TYPES.TIRES &&
              !infoMessage && (
                <TreadwellCompactBanner styleName="treadwell-compact-banner" />
              )}
          </PageTitle>
        )}
        {(isLoading || window.isPreRendering) && (
          <>
            <PageTitle styleName="heading-empty" title=" " />
            <div styleName="loading-cover" />
          </>
        )}
        {showFitmentPageBanner ? (
          <FitmentPageBanner
            doesFitEnum={allProductsDoesFitEnum}
            isLoading={!allProductsDoesFitEnum}
            isPackage={isPrebuiltPackageSearch}
            isTextSearch={isTextSearch}
            productType={productType}
          />
        ) : (
          <hr styleName="vehicle-divider" />
        )}

        <Content id="product-list-content" styleName="content">
          {showTopThree && !breakpoint.isMinMd && (
            <TopThree staggeredSearchType={staggeredSearchType} />
          )}

          <div
            styleName={
              selectedFitment &&
              productType === PRODUCT_TYPES.TIRES &&
              isTreadwellFlow
                ? 'product-list__treadwell'
                : 'product-list'
            }
          >
            {breakpoint.isMinSm && (
              <div styleName="left-column">
                <div styleName="product-list-filter">
                  <DTCCBanner
                    applyNowButtonId="product-list-page-dtcc-promo"
                    linkText="Learn More"
                    styleName="dtcc-banner"
                  />
                  <Suspense fallback={<div />}>
                    {filters && !window.isPreRendering && (
                      <ProductListFilter
                        accessoryCategory={category?.code}
                        defaultMaxPrice={defaultMaxPrice}
                        defaultMinPrice={defaultMinPrice}
                        filters={filters}
                        highestPriceInResults={highestPriceInResults}
                        isAccessorySearch={isAccessorySearch}
                        isLoadingPLP={isLoading}
                        isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                        isTextSearch={isTextSearch}
                        lowestPriceInResults={lowestPriceInResults}
                        onSelectFilter={updateSelectedFilters}
                        onSliderValueChange={onSliderValueChange}
                        selectedFilters={selectedFilters}
                        toggleType="CHEVRON"
                      />
                    )}
                  </Suspense>
                </div>
              </div>
            )}

            <div id="product-list" styleName="right-column">
              {filters && (
                <ProductListFilterModal
                  categoryCode={category?.code}
                  defaultMaxPrice={defaultMaxPrice}
                  defaultMinPrice={defaultMinPrice}
                  filters={filters}
                  highestPriceInResults={highestPriceInResults}
                  isAccessorySearch={isAccessorySearch}
                  isOpen={Boolean(displayMobileFilters) && breakpoint.isXS}
                  isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                  isTextSearch={isTextSearch}
                  lowestPriceInResults={lowestPriceInResults}
                  onClearSelectedFilters={clearSelectedFilters}
                  onClose={onToggleMobileFilter}
                  onUpdateSelectedFilters={updateSelectedFilters}
                  selectedFilters={selectedFilters}
                />
              )}

              <div styleName="inner">
                <div styleName="top-section">
                  {showTopThree && breakpoint.isMinMd && (
                    <TopThree staggeredSearchType={staggeredSearchType} />
                  )}
                  {!isLoading && isSizeSearch && isStaggeredSearch && (
                    <StaggeredSearchInfo
                      tireSizes={tireSizes}
                      wheelSizes={wheelSizes}
                    />
                  )}
                  {productType === PRODUCT_TYPES.WHEELS && !isPackageSearch && (
                    <WheelConfiguratorBanner />
                  )}

                  {infoMessage && (
                    <Message
                      isAlignCenter
                      isInfo
                      message={
                        <Typography size="small" weight="heavy">
                          {infoMessage.id ===
                            PLPInfoMessageIds.noInStockTreadwellProducts &&
                            'There are no Treadwell tested tires in stock at your store, but here are the best tires for your size!'}
                          {infoMessage.id ===
                            PLPInfoMessageIds.noTreadwellProducts &&
                            "While Treadwell hasn't tested tires for this vehicle yet, these tires are a great fit!"}
                        </Typography>
                      }
                      styleName="treadwell-message"
                    />
                  )}

                  {productType === PRODUCT_TYPES.TIRES &&
                    breakpoint.isMinSm && (
                      <WinterTiresBanner
                        filters={filters}
                        formattedTireSize={formattedTireSize}
                        selectedFilters={selectedFilters}
                      />
                    )}
                  <DTCCBanner
                    applyNowButtonId="product-list-page-dtcc-promo"
                    linkText="Learn More"
                    styleName="dtcc-banner-mobile"
                  />
                  <ResultsBar
                    currentPage={pageNum}
                    currentSort={isLoading ? 'bestSeller-asc' : currentSort}
                    filterCount={selectedFilters?.length}
                    formattedTireSize={formattedTireSize}
                    hideSize={isSizeSearch && isStaggeredSearch}
                    isTreadwellFlow={isTreadwellFlow}
                    numberOfPages={window.isPreRendering ? 0 : numberOfPages}
                    onPageChange={onPageChange}
                    onSelectSort={sort => onSelectSort(sort)}
                    onToggleMobileFilter={onToggleMobileFilter}
                    recommendationAvailable={recommendationAvailable}
                    resultsText={isLoading ? null : getResultsText(products)}
                    showFilter
                    sorts={
                      isLoading || window.isPreRendering
                        ? {
                            'bestSeller-asc': {
                              label: ' ',
                              value: ''
                            }
                          }
                        : sortOptions
                    }
                    totalNumberOfResults={
                      isLoading ? ' ' : totalNumberOfResults
                    }
                    wheelSize={formatWheelSizeParts(wheelSizes?.front)}
                  />
                  {productType === PRODUCT_TYPES.TIRES &&
                    !breakpoint.isMinSm && (
                      <WinterTiresBanner
                        filters={filters}
                        formattedTireSize={formattedTireSize}
                        selectedFilters={selectedFilters}
                      />
                    )}
                  {isStaggeredSearch &&
                    !isPackageSearch &&
                    !isLoading &&
                    (!isArrayWithLength(products) ||
                      search === `?${FILTERS_CLEARED_QUERY_PARAM_NAME}`) && (
                      <Message
                        isLight
                        isWarning
                        message={
                          search === `?${FILTERS_CLEARED_QUERY_PARAM_NAME}`
                            ? 'Your previous search filter(s) has been removed due to no results found.'
                            : 'There are no sets found for the size entered. Please try another size.'
                        }
                      />
                    )}

                  {isPackageSearch && (
                    <div styleName="tire-wheel-package-details">
                      <hr styleName="tire-wheel-package-divider" />
                      <TireWheelPackageDetails />
                    </div>
                  )}
                </div>

                {isStaggeredSearch && !isPackageSearch && (
                  <StaggeredProductTabs
                    isSizeSearch={isSizeSearch}
                    onTabClick={onTabClick}
                    productType={productType}
                    staggeredSearchType={staggeredSearchType}
                    tireSizes={tireSizes}
                    wheelSizes={wheelSizes}
                  />
                )}

                {productSetSearchType && (
                  <SetProductTabs
                    doesFitEnum={allProductsDoesFitEnum}
                    productSetSearchType={productSetSearchType}
                  />
                )}

                {Array.isArray(selectedFilters) &&
                  selectedFilters.length > 0 && (
                    <FilterChips
                      className="display-sm"
                      filters={selectedFilters?.filter(isChipFilter)}
                      onChipClick={updateSelectedFilters}
                      onClearAllClick={clearSelectedFilters}
                      showClearAll
                      styleName="filter-chips"
                    />
                  )}

                <SectionLoadingSpinner isActive shouldSpinOnRender>
                  {process.env.__FF_LOAD_MORE__ === 'true' ? (
                    <LoadMore
                      comparePreviewRef={comparePreviewRef}
                      isLoading={isLoading}
                      isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                      numberOfPages={numberOfPages}
                      pageNum={pageNum}
                      products={products}
                      setPageNum={setPageNum}
                      setTreadwellProductsDisplayType={
                        setTreadwellProductsDisplayType
                      }
                      totalNumberOfResults={totalNumberOfResults}
                      treadwellProductsDisplayType={
                        treadwellProductsDisplayType
                      }
                    >
                      <div styleName="list-wrapper">
                        <ProductList
                          isPackageSearch={isPackageSearch}
                          isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                          isStaggeredSearch={selectedFitment?.isStaggered}
                          isTreadwellFlow={isTreadwellFlow}
                          onAddProductClick={onAddProductClick}
                          onCheckInventoryClick={product =>
                            trackAction(
                              'product_list_check_nearby_inventory',
                              getProductEntryDataLayerValues(product)
                            )
                          }
                          onViewProductClick={onViewProductClick}
                          prebuiltPackagesAndPageNum={
                            isPrebuiltPackageSearch ? products : undefined
                          }
                          productSetSearchType={productSetSearchType}
                          productsAndPageNum={
                            !isPrebuiltPackageSearch ? products : undefined
                          }
                        />
                      </div>
                    </LoadMore>
                  ) : (
                    <div styleName="list-wrapper">
                      <ProductList
                        isPackageSearch={isPackageSearch}
                        isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                        isStaggeredSearch={selectedFitment?.isStaggered}
                        isTreadwellFlow={isTreadwellFlow}
                        onAddProductClick={onAddProductClick}
                        onCheckInventoryClick={product =>
                          trackAction(
                            'product_list_check_nearby_inventory',
                            getProductEntryDataLayerValues(product)
                          )
                        }
                        onViewProductClick={onViewProductClick}
                        prebuiltPackages={
                          isPrebuiltPackageSearch ? products : undefined
                        }
                        productSetSearchType={productSetSearchType}
                        products={
                          !isPrebuiltPackageSearch ? products : undefined
                        }
                      />
                    </div>
                  )}
                </SectionLoadingSpinner>
              </div>
              <TreadwellSeeAllButton
                onClick={() => {
                  setTreadwellProductsDisplayType(
                    TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
                  );
                  updatePlpAnalytics(allProducts, false);
                }}
                styleName="recommended-tires-wrapper"
                treadwellProductsDisplayType={treadwellProductsDisplayType}
              />
              {process.env.__FF_LOAD_MORE__ !== 'true' &&
                numberOfPages > 0 &&
                !window.isPreRendering && (
                  <Pagination
                    currentPage={pageNum}
                    numberOfPages={numberOfPages}
                    onPageChange={onPageChange}
                    styleName="pagination"
                  />
                )}
            </div>
          </div>
          {!isLoading && productsForComparison?.length > 0 && (
            <ProductComparePreview
              maxProductsForComparison={maxProductsForComparison}
              onClearAll={clearAllProductsForComparison}
              onClearProduct={onProductComparison}
              productsForComparison={productsForComparison}
              ref={comparePreviewRef}
            />
          )}
        </Content>
      </Page>
    </DefaultLayout>
  );
}

ProductListPage.propTypes = {
  isAccessorySearch: PropTypes.bool,
  isBrandSearch: PropTypes.bool,
  isFitmentSearch: PropTypes.bool,
  isPackageSearch: PropTypes.bool,
  isPrebuiltPackageSearch: PropTypes.bool,
  isSizeSearch: PropTypes.bool,
  isStaggeredSearch: PropTypes.bool,
  isTextSearch: PropTypes.bool,
  productType: ProductTypeProp,
  queryType: PropTypes.string,
  searchType: PropTypes.oneOf([
    'PLP',
    'BRAND_SEARCH',
    'SIZE_SEARCH',
    'CATEGORY_SEARCH'
  ])
};

export default ProductListPage;
